import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";

// Core Values Data
const CoreValuesData = [
  {
    id: 1,
    name: "Commitment",
    description: "We go all in for every client—delivering on our promises with consistency and dedication, no matter the scope.",
    color: "#264653",
    gradient: "linear-gradient(135deg, #264653 0%, #1a3240 100%)"
  },
  {
    id: 2,
    name: "Accountability",
    description: "We own our outcomes. When something goes wrong, we address it head-on and make it right.",
    color: "#2a9d8f",
    gradient: "linear-gradient(135deg, #2a9d8f 0%, #1e7468 100%)"
  },
  {
    id: 3,
    name: "Transparency",
    description: "We communicate openly—no hidden agendas, no surprises. Clients always know where things stand.",
    color: "#e76f51",
    gradient: "linear-gradient(135deg, #e76f51 0%, #d55a3a 100%)"
  },
  {
    id: 4,
    name: "Problem Solving",
    description: "We don't just flag issues—we bring solutions. Every challenge is an opportunity to think sharper and move faster.",
    color: "#f4a261",
    gradient: "linear-gradient(135deg, #f4a261 0%, #e8924a 100%)"
  }
];

const CoreValuesSection = () => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.3
      }
    }
  };

  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 40,
      scale: 0.95
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  return (
    <section className="relative py-12 md:py-16 px-6 bg-white overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-[0.015]">
        <div className="absolute top-0 left-0 w-full h-full" 
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, #1b7840 1px, transparent 0)`,
            backgroundSize: '48px 48px'
          }}
        />
      </div>

      {/* Decorative Background Blobs */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 0.04, scale: 1 }}
        transition={{ duration: 1.2 }}
        viewport={{ once: true }}
        className="absolute top-20 right-10 w-96 h-96 rounded-full bg-primary blur-3xl"
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 0.04, scale: 1 }}
        transition={{ duration: 1.2, delay: 0.3 }}
        viewport={{ once: true }}
        className="absolute bottom-20 left-10 w-96 h-96 rounded-full bg-orange-400 blur-3xl"
      />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          
          {/* Left Column - Content (Sticky) */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="lg:sticky lg:top-24 space-y-5">
              {/* Tag */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="inline-block"
              >
                <span className="text-xs md:text-sm font-bold text-primary uppercase tracking-wider">
                  What Drives Us
                </span>
              </motion.div>

              {/* Main Heading */}
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                viewport={{ once: true }}
                className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 leading-[1.05]"
                style={{ letterSpacing: '-0.02em' }}
              >
                Core
                <br />
                <span className="relative inline-block">
                  <span className="relative z-10 italic font-serif" style={{ color: "#1b7840" }}>Values</span>
                  <svg
                    className="absolute -bottom-2 left-0 w-full h-3"
                    viewBox="0 0 200 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    preserveAspectRatio="none"
                  >
                    <motion.path
                      d="M2 8C50 2, 150 2, 198 8"
                      stroke="#1b7840"
                      strokeWidth="4"
                      strokeLinecap="round"
                      initial={{ pathLength: 0 }}
                      whileInView={{ pathLength: 1 }}
                      transition={{ duration: 1, delay: 0.6 }}
                      viewport={{ once: true }}
                    />
                  </svg>
                </span>
              </motion.h2>

              {/* Description */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                viewport={{ once: true }}
                className="text-lg md:text-xl text-gray-600 leading-relaxed max-w-xl"
              >
                The non-negotiable principles that guide our partnerships and define our culture
              </motion.p>

              {/* Decorative Quote Box */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-primary/5 to-transparent p-5 rounded-2xl border border-primary/10"
              >
                <div className="flex items-start gap-3">
                  <Sparkles className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <p className="text-gray-700 text-base md:text-lg leading-relaxed italic">
                    "Our values aren't just words on a wall—they're the foundation of every decision we make and every relationship we build."
                  </p>
                </div>
              </motion.div>

              {/* CTA Button */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                viewport={{ once: true }}
              >
                <Link to="/contact">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="group relative px-9 py-4 bg-primary hover:bg-primary-dark text-white rounded-full font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2 overflow-hidden"
                  >
                    <span className="relative z-10 flex items-center gap-2">
                      Partner With Us
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </span>
                    <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></span>
                  </motion.button>
                </Link>
              </motion.div>
            </div>
          </motion.div>

          {/* Right Column - Values Cards (Scrollable) */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="space-y-3"
          >
            {CoreValuesData.map((value, index) => (
              <motion.div
                key={value.id}
                variants={cardVariants}
                whileHover={{ 
                  scale: 1.02,
                  x: 8,
                  transition: { duration: 0.3 }
                }}
                className="group relative overflow-hidden rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer"
              >
                {/* Background with gradient */}
                <div 
                  className="absolute inset-0 opacity-90 group-hover:opacity-100 transition-opacity duration-500"
                  style={{ background: value.gradient }}
                />

                {/* Shine effect on hover */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"
                  initial={{ x: '-100%' }}
                  whileHover={{ x: '100%' }}
                  transition={{ duration: 0.8 }}
                />

                {/* Content */}
                <div className="relative z-10 p-5 md:p-6">
                  {/* Number Badge */}
                  <motion.div
                    className="inline-flex items-center justify-center w-8 h-8 mb-3 rounded-lg bg-white/20 backdrop-blur-sm"
                    whileHover={{ rotate: 360, scale: 1.1 }}
                    transition={{ duration: 0.6 }}
                  >
                    <span className="text-white font-bold text-sm">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                  </motion.div>

                  {/* Title */}
                  <motion.h3
                    className="text-xl md:text-2xl font-bold text-white mb-2 leading-tight"
                    style={{ letterSpacing: '-0.01em' }}
                  >
                    {value.name}
                  </motion.h3>

                  {/* Description */}
                  <p className="text-white/90 text-sm md:text-base leading-relaxed">
                    {value.description}
                  </p>

                  {/* Decorative Element */}
                  <motion.div
                    className="absolute top-6 right-6 w-20 h-20 rounded-full bg-white/10 blur-2xl"
                    animate={{
                      scale: [1, 1.2, 1],
                      opacity: [0.3, 0.5, 0.3]
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  />
                </div>

                {/* Bottom accent line */}
                <motion.div
                  className="absolute bottom-0 left-0 h-1 bg-white/40"
                  initial={{ width: 0 }}
                  whileInView={{ width: '100%' }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default CoreValuesSection;